import { CloseOutlined, RedoOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Select } from 'antd';
import { type Dispatch, type SetStateAction, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import AIIcon from '@/components/Badge/AIBadge/AIIcon';
import { CenterFlex, Flex, Space, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import Checkbox from '@/components/Checkbox/Checkbox';
import Spinner from '@/components/Common/Spinner';
import List from '@/components/List/List';
import Rotator from '@/components/Rotator/Rotator';
import Tooltip from '@/components/Tooltip/TooltipV2';
import AnimateText from '@/components/Typography/AnimatedText';
import Heading from '@/components/Typography/Heading';
import Typo from '@/components/Typography/Typo';
import queryKeys from '@/configs/react-query-keys';
import service from '@/services/index';
import { modalStyle } from '@/styles/style';
import { TOPIC_2_TYPE } from '@/types/taxonomyConst';
import { getPercentStringFormat, stringSort } from '@/utils/util';

import type { CandidateItemT } from '../CreateTopicModal/AiTopic/types';
import BarItemStyle from './BarItem';
import CoverageChart from './CoverageChart';
import { type AISuggestionBaseProps, type AISuggestionTarget, Entity } from './types';

enum ViewMode {
  BUBBLE,
  BAR,
}

const SuggestionStep = <T extends AISuggestionTarget>(
  props: AISuggestionBaseProps<T> & {
    entityList: Entity[];
    entity: Entity;
    setEntity: Dispatch<SetStateAction<Entity>>;
    onNextClick: () => void;
    selectedItems: CandidateItemT[];
    setSelectedItems: Dispatch<SetStateAction<CandidateItemT[]>>;
    initState: [boolean, Dispatch<SetStateAction<boolean>>];
    closeModal: () => void;
  },
) => {
  const [init, setInit] = props.initState;
  const { setSelectedItems } = props;
  const [viewMode, _] = useState<ViewMode>(ViewMode.BAR);

  const handleEntityChange = (value: Entity) => {
    setSelectedItems([]);
    setTimeout(() => {
      props.setEntity(value);
    }, 300);
  };

  const coverageFetcher = async (alias: string) => {
    switch (props.target) {
      case 'taxonomy': {
        const promises = [
          service.taxonomy.fetchTaxonomyFeedbackCount(alias),
          service.taxonomy.fetchTaxonomyUnclassifiedFeedbackCount(alias),
        ];
        const res = await Promise.all(promises);

        return {
          total_count: res[0].count,
          unsubsclassification_count: res[1].count,
        };
      }

      case 'topic': {
        const promises = [
          service.taxonomy.fetchTopicFeedbackCount(alias),
          service.taxonomy.fetchTopicUnclassifiedFeedbackCount(alias),
        ];
        const res = await Promise.all(promises);

        return {
          total_count: res[0].count,
          unsubsclassification_count: res[1].count,
        };
      }

      default:
        throw new Error('Invalid target type');
    }
  };

  const coverageQuery = useQuery({
    queryFn: () => coverageFetcher(props.targetAlias),
    queryKey: [
      'coverage-data',
      props.target,
      props.targetAlias,
      props.taxonomyAlias,
    ],
  });

  const suggestionQuery = useQuery({
    queryFn: () =>
      service.taxonomy.fetchAISuggestionTopic({
        entity_type: props.entity,
        parent_topic_id:
          props.target === 'topic' ? props.targetAlias : undefined,
        taxonomy_id: props.taxonomyAlias,
      }),
    queryKey: [
      queryKeys.taxonomy.AI_SUGGESTION_LIST,
      props.entity,
      props.target,
      props.targetAlias,
      props.taxonomyAlias,
    ],
    staleTime: Infinity,
  });

  const slicedData = useMemo(
    () =>
      suggestionQuery.data
        ?.sort((a, b) => {
          const aValue = a.count || 0;
          const bValue = b.count || 0;

          if (aValue === bValue) {
            return stringSort(a.name, b.name);
          }
          return bValue - aValue;
        })
        .slice(0, 30) || null,
    [suggestionQuery.data],
  );

  const suggestionMaxCount = useMemo(() => {
    return (
      slicedData?.reduce((acc, value) => {
        if (acc < (value.count || 0)) {
          acc = value.count || 0;
        }
        return acc;
      }, 0) || 0
    );
  }, [slicedData]);

  const suggestionTotalCount = useMemo(() => {
    return (
      slicedData?.reduce((acc, value) => {
        acc += value.count || 0;
        return acc;
      }, 0) || 0
    );
  }, [slicedData]);

  useEffect(() => {
    if (!init && slicedData) {
      setSelectedItems(
        slicedData.map((d) => ({
          name: d.name,
          category: TOPIC_2_TYPE.AI,
          ai_rule: {
            prompt: '',
            prompt_en: '',
            filter: undefined,
          },
          readyState: false,
          isLoading: false,
          promptChanged: false,
          isError: false,
        })),
      );
      setInit(true);
    }
  }, [slicedData, setSelectedItems, init, setInit]);

  const selectStatus = useMemo(() => {
    if (props.selectedItems.length === 0) return 'none';
    if (props.selectedItems.length === slicedData?.length) return 'all';
    return 'half';
  }, [props.selectedItems.length, slicedData?.length]);

  return (
    <Style a="center" g={24}>
      <IconButton
        className="btn-close"
        icon={<CloseOutlined />}
        sizeType="l"
        outlined={false}
        onClick={props.closeModal}
      />
      <VFlex g={14} a="center">
        <Heading type="h2" multilineEllipsis={1} style={{ maxWidth: 800 }}>
          Choose topics related to "{props.targetName}"
        </Heading>
        <Typo type="body2">
          Syncly AI curates the topics based on the dataset
        </Typo>
      </VFlex>
      <VFlex f={1} width="100%" a="center" style={{ overflow: 'auto' }}>
        {/* <Flex j="flex-end" g={16} width="100%">
          <Radio.Group
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            size="middle"
          >
            <Radio.Button value={ViewMode.BUBBLE}>
              <ExperimentFilled />
            </Radio.Button>
            <Radio.Button value={ViewMode.BAR}>
              <DetailViewIcon style={{ transform: 'translateY(3px)' }} />
            </Radio.Button>
          </Radio.Group>
        </Flex> */}
        <VFlex
          f={1}
          width="680px"
          padding="20px 0"
          style={{ overflow: 'auto' }}
        >
          <Flex j="space-between" a="center">
            <Flex g={6} a="center">
              <AIIcon />
              <Typo type="body1" weight="sb">
                Suggested topics by Syncly
              </Typo>
            </Flex>
            <Select
              style={{ width: 140 }}
              value={props.entity}
              onChange={handleEntityChange}
              options={props.entityList.map((v) => {
                return {
                  value: v,
                  label: Entity[v],
                };
              })}
            />
          </Flex>
          <Space h={32} />
          <Flex j="space-between">
            <Flex
              a="center"
              g={12}
              style={{ cursor: 'pointer' }}
              onClick={() => {
                if (selectStatus === 'all') {
                  setSelectedItems([]);
                  return;
                }
                setSelectedItems(
                  slicedData?.map((t) => ({
                    name: t.name,
                    category: TOPIC_2_TYPE.AI,
                    ai_rule: {
                      prompt: '',
                      prompt_en: '',
                      filter: undefined,
                    },
                    readyState: false,
                    isLoading: false,
                    promptChanged: false,
                    isError: false,
                  })) || [],
                );
              }}
            >
              <Checkbox
                checked={selectStatus !== 'none'}
                halfCheck={selectStatus === 'half'}
              />
              <Typo type="label2" weight="m">
                Select all
              </Typo>
            </Flex>
            <Tooltip tooltipNode="Refresh suggestions">
              <Flex>
                <IconButton
                  sizeType="m"
                  icon={<RedoOutlined />}
                  onClick={suggestionQuery.refetch}
                  outlined={false}
                />
              </Flex>
            </Tooltip>
          </Flex>
          <Space h={16} />
          {viewMode === ViewMode.BAR && (
            <List
              isLoading={suggestionQuery.isFetching}
              loader={
                <CenterFlex d="column" g={12} f={1}>
                  <Spinner />
                  <Rotator
                    interval={10000}
                    repeat={false}
                    items={[
                      'Scanning data set...',
                      'Curating topics...',
                      'Almost done...',
                    ].map((v) => (
                      <AnimateText
                        className="text-gray-600"
                        repeatInterval={5500}
                        textInterval={100}
                      >
                        {v}
                      </AnimateText>
                    ))}
                  />
                </CenterFlex>
              }
              data={slicedData || []}
              className="gap-3"
              render={(t, index) => {
                const shares = getPercentStringFormat(
                  suggestionTotalCount,
                  t.count || 0,
                );
                const width = getPercentStringFormat(
                  suggestionMaxCount,
                  t.count || 0,
                );
                return (
                  <BarItemStyle
                    key={`${t.name}_${index}`}
                    name={t.name}
                    width={width}
                    share={shares}
                    selected={
                      !!props.selectedItems.find((item) => item.name === t.name)
                    }
                    onClick={() => {
                      const newArr = [...props.selectedItems];
                      const idx = newArr.findIndex((v) => v.name === t.name);
                      if (idx > -1) {
                        newArr.splice(idx, 1);
                      } else {
                        newArr.push({
                          name: t.name,
                          category: TOPIC_2_TYPE.AI,
                          ai_rule: {
                            prompt: '',
                            prompt_en: '',
                            filter: undefined,
                          },
                          readyState: false,
                          isLoading: false,
                          promptChanged: false,
                          isError: false,
                        });
                      }
                      setSelectedItems(newArr);
                    }}
                  />
                );
              }}
            />
          )}
          {/* 
          {viewMode === ViewMode.BUBBLE && (
            <BubbleChart
              data={slicedData.map((d) => ({
                id: d.name,
                selected: !!props.selectedItems.find(
                  (item) => item.name === d.name,
                ),
                name: d.name,
                value: d.count || 0,
              }))}
            />
          )} */}
          <Space h={16} />
          <VFlex g={10}>
            <CoverageChart
              data={slicedData || []}
              coverageData={coverageQuery.data}
              selectedList={props.selectedItems.map((v) => v.name)}
            />
          </VFlex>
        </VFlex>
      </VFlex>
      <Flex>
        <Button variant="primary" sizeType="l" onClick={props.onNextClick}>
          Next
        </Button>
      </Flex>
    </Style>
  );
};

const Style = styled(VFlex)`
  position: relative;
  width: 1000px;
  height: 800px;
  padding: 52px 32px 36px;
  overflow: auto;
  ${modalStyle};
  .btn-close {
    position: absolute;
    right: 24px;
    top: 24px;
  }
`;

export default SuggestionStep;
